import { Link } from "react-router-dom";
import TrackedLink from "@/components/TrackedLink";
import { Button } from "@/components/ui/button";
import FAQ from "@/components/FAQ";
import { veiligMailenZorgFAQ } from "@/data/faqs";
import { trackCTA } from "@/lib/analytics";
import {
  MailCheck,
  ShieldAlert,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  AtSign,
  Inbox,
  Shuffle,
  Building2,
  User,
  Stethoscope,
  Truck,
  Smartphone,
  Monitor,
  BadgeCheck,
  HeartHandshake,
  Globe,
  FileCheck2,
  ClipboardList,
  ArrowRight,
  Users,
  MailOpen,
} from "lucide-react";
import { motion } from "framer-motion";
import heroBg from "@/assets/veilig-mailen-zorg-hero-bg.jpg";
import outlookIcon from "@/assets/ms-icons/outlook.svg";

const scenarios = [
  { icon: AtSign, title: "Twee adressen op je visitekaartje", description: "Eén adres voor gewone mail en één voor veilige mail. Lastig voor jezelf en verwarrend voor patiënten en collega's." },
  { icon: Inbox, title: "Twee mailboxen in de gaten houden", description: "Veilige mail staat in een aparte omgeving. Dus elke dag extra inloggen om te kijken of er iets binnen is." },
  { icon: Shuffle, title: "Bij elke mail opnieuw kiezen", description: "Moet dit via het veilige adres of mag het gewoon? Eén keer verkeerd gekozen en gevoelige gegevens gaan onbeveiligd de deur uit." },
  { icon: Building2, title: "Alleen op de praktijk, of via een VPN", description: "Sommige oplossingen werken alleen op de praktijk of via een lastige VPN-verbinding. Thuis of onderweg even veilig mailen? Dat lukt dan niet." },
];

const situaties = [
  { icon: User, title: "Je mailt een patiënt", description: "Klik op Veilig verzenden. De patiënt krijgt een melding per mail en een persoonlijke code per sms. Daarmee opent hij of zij het bericht in een beveiligde omgeving en kan veilig reageren. Geen 06-nummer bij de hand? Dan komt de code per mail." },
  { icon: Stethoscope, title: "Je mailt een andere zorgverlener", description: "Is de huisarts, apotheek, het ziekenhuis of de collega ook aangesloten? Dan gaat je mail vanzelf veilig en hoeft de ontvanger geen code in te voeren." },
  { icon: MailOpen, title: "Je ontvangt mail van een andere zorgverlener", description: "Stuurt een aangesloten zorgverlener jou veilige mail? Die komt gewoon binnen in je eigen inbox. Geen code, geen aparte omgeving en geen extra inlog. Je leest en beantwoordt het bericht zoals elke andere mail." },
  { icon: Truck, title: "Je mailt een leverancier of iemand anders", description: "Gewoon mailen, vanaf hetzelfde adres. Zonder code en zonder extra stappen. Niet elke mail is immers gevoelig." },
];

const comparison = {
  without: [
    "Twee mailadressen en twee mailboxen",
    "Bij elke mail kiezen: veilig of gewoon?",
    "Apart inloggen om veilige mail te lezen",
    "Kans op fouten door het verkeerde adres",
    "Vaak alleen op de praktijk of via een VPN",
  ],
  with: [
    "Eén mailadres voor alles",
    "Veilig mailen met één klik, vanuit je eigen Outlook",
    "Veilige mail van collega's komt in je gewone inbox",
    "Mail naar aangesloten zorgverleners gaat vanzelf veilig",
    "Overal: computer, web en telefoon",
  ],
};

const voordelen = [
  { icon: Inbox, title: "Eén adres, één inbox", description: "Geen tweede mailadres en geen aparte mailbox meer. Alles staat overzichtelijk bij elkaar." },
  { icon: Monitor, title: "Gewoon in je eigen Outlook", description: "Op Windows en Mac, in Outlook op het web en op je telefoon. Je hoeft niets nieuws te leren." },
  { icon: BadgeCheck, title: "Zekerheid over de ontvanger", description: "Aangesloten zorgverleners zijn vooraf gecontroleerd. Je weet dus met wie je mailt." },
  { icon: HeartHandshake, title: "Makkelijk voor patiënten", description: "Patiënten openen je bericht met een code per sms. Een account of app is niet nodig." },
  { icon: Globe, title: "Overal, zonder VPN", description: "Op de praktijk, thuis of onderweg. Geen ingewikkelde verbinding nodig." },
  { icon: ShieldCheck, title: "Volgens de norm", description: "De techniek richten we in volgens NTA 7516, de norm voor veilig mailen in de zorg." },
];

const wijRegelen = [
  "Tweestapsverificatie op alle accounts",
  "De beveiliging van je mailadres en domeinnaam (zoals DKIM, DMARC en DNSSEC)",
  "Het kenmerk in je domeinnaam dat laat zien dat je volgens de norm mailt",
  "De knop Veilig verzenden in Outlook, voor iedereen in de praktijk",
  "De koppeling met de leverancier, getest en wel",
];

const zelfDoen = [
  "De verklaring tekenen dat je praktijk aan de norm voldoet (die krijg je van de leverancier)",
  "Afspraken maken in de praktijk over veilig mailen",
  "06-nummers van patiënten vastleggen, zodat de code per sms kan",
];

const stappen = [
  { step: "1", title: "Kennismaken", text: "We bespreken hoe jullie nu mailen en wat je nodig hebt. Kort en zonder verkooppraatje." },
  { step: "2", title: "Jij tekent het contract met de leverancier", text: "Je krijgt een offerte rechtstreeks van de leverancier. Akkoord? Dan teken je het contract met hen. Dat is het enige wat je zelf hoeft te doen." },
  { step: "3", title: "Wij koppelen en testen", text: "Wij schakelen met de leverancier en richten Microsoft 365, je domeinnaam en Outlook in. Daarna testen we of alles goed werkt." },
  { step: "4", title: "Werkend opgeleverd", text: "We leveren het werkend op en leggen kort uit hoe het werkt. Vragen achteraf? Gewoon bellen." },
];

const doelgroepen = [
  "Huisartsen en gezondheidscentra",
  "Fysiotherapeuten, tandartsen en andere paramedici",
  "GGZ-praktijken, psychologen en jeugdzorg",
  "Thuiszorg en kleinere zorgorganisaties",
];

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const PraktijkVeiligMailenZorg = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[420px] overflow-hidden bg-black text-primary-foreground">
        <img loading="eager" decoding="async" fetchPriority="high" src={heroBg} width={1920} height={1080} alt="Veilig mailen in de zorg vanuit je eigen Outlook" className="absolute inset-0 h-full w-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
        <div className="container relative z-10 py-20 md:py-32">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 backdrop-blur-sm"
            >
              <MailCheck className="h-7 w-7 text-rinqel-green" />
            </motion.div>
            <span className="mb-4 inline-block rounded-full bg-rinqel-green/20 px-4 py-1.5 text-sm font-semibold text-rinqel-green">
              Praktijkvoorbeeld · Zorg
            </span>
            <h1 className="mb-6 text-4xl font-bold leading-tight md:text-5xl lg:text-6xl">
              Veilig mailen in de zorg?{" "}
              <span className="text-rinqel-green">Gewoon</span> <span className="text-primary">met je eigen mailadres.</span>
            </h1>
            <p className="mb-8 text-lg text-primary-foreground/80 md:text-xl">
              Veel zorgverleners werken nog met twee mailadressen: één voor gewone mail en één voor veilige mail. Dat betekent twee mailboxen bijhouden en bij elke mail kiezen. Het kan veel simpeler: één adres, gewoon in je eigen Outlook, en veilig waar het moet.
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              {["Eén mailadres voor alles", "Gewoon vanuit je eigen Outlook", "Veilig mailen met patiënten", "Overal, zonder VPN"].map((item, i) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 + i * 0.1 }}
                  className="flex items-center gap-2 text-sm text-primary-foreground/90"
                >
                  <CheckCircle2 className="h-4 w-4 text-rinqel-green" />
                  <span>{item}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Herkenning */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">Herken je dit?</h2>
            <p className="mb-12 text-lg text-muted-foreground">
              Veilig mailen hoort bij de zorg. Maar vaak is het onhandig geregeld.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {scenarios.map((s, i) => (
              <motion.div
                key={s.title}
                {...fadeUp}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="group rounded-2xl border border-border bg-card p-6 transition-all hover:border-destructive/30 hover:shadow-lg"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-destructive/10">
                  <s.icon className="h-6 w-6 text-destructive" />
                </div>
                <h3 className="mb-2 text-lg font-bold">{s.title}</h3>
                <p className="text-muted-foreground">{s.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Het probleem */}
      <section className="border-y border-border bg-muted/30 py-16 md:py-24">
        <div className="container">
          <div className="mx-auto max-w-3xl">
            <div className="mb-8 flex h-16 w-16 items-center justify-center rounded-2xl bg-destructive/10">
              <ShieldAlert className="h-8 w-8 text-destructive" />
            </div>
            <h2 className="mb-6 text-3xl font-bold md:text-4xl">Waarom is dat een probleem?</h2>
            <div className="space-y-4 text-lg text-muted-foreground">
              <p>
                Gezondheidsgegevens zijn extra gevoelig. Mail met zulke informatie moet daarom veilig de deur uit; dat staat ook in de norm NTA 7516.
              </p>
              <p>
                Maar hoe meer gedoe, hoe groter de kans dat het misgaat. Een mail via het verkeerde adres. Een bijlage die toch gewoon wordt verstuurd. Een bericht dat dagen in een vergeten mailbox blijft staan.
              </p>
              <p>
                Veilig mailen moet dus vooral makkelijk zijn. <strong className="text-foreground">En dat kan.</strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* De oplossing */}
      <section className="bg-rinqel-green/5 py-16 md:py-24">
        <div className="container">
          <div className="mx-auto max-w-3xl">
            <div className="mb-8 flex h-16 w-16 items-center justify-center rounded-2xl bg-rinqel-green/10">
              <ShieldCheck className="h-8 w-8 text-rinqel-green" />
            </div>
            <h2 className="mb-6 text-3xl font-bold md:text-4xl">Eén adres. Gewoon in Outlook. Veilig waar het moet.</h2>
            <div className="space-y-4 text-lg text-muted-foreground">
              <p>
                Wij koppelen je Microsoft 365-omgeving aan een gerenommeerde leverancier van veilige mail voor de zorg. Daardoor werkt veilig mailen gewoon in de Outlook die je al gebruikt, met je eigen vertrouwde mailadres.
              </p>
              <p>
                Je wisselt niet meer tussen mailboxen en je hebt maar één adres op je visitekaartje. Mail naar aangesloten zorgverleners gaat vanzelf veilig. Naar een patiënt verstuur je met één klik veilig.
              </p>
              <p>
                En het werkt overal: op de praktijk, thuis of onderweg. Op je computer (Windows en Mac), in Outlook op het web en zelfs op je telefoon. Geen VPN, geen gedoe.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Uitbreiding op Exchange Online */}
      <section className="border-y border-border bg-muted/30 py-16 md:py-24">
        <div className="container">
          <motion.div
            {...fadeUp}
            className="mx-auto max-w-3xl rounded-2xl border border-border bg-card p-8 shadow-card md:p-10"
          >
            <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-muted">
              <img loading="lazy" decoding="async" src={outlookIcon} alt="Outlook" className="h-9 w-9 object-contain" />
            </div>
            <h2 className="mb-6 text-3xl font-bold md:text-4xl">Een ingebakken uitbreiding op Exchange Online</h2>
            <div className="space-y-4 text-lg text-muted-foreground">
              <p>
                Veilig mailen is geen los programma. Het is een uitbreiding die we inbakken in je zakelijke e-mail met Exchange Online. Je mailadres, je mailbox en je Outlook blijven precies hetzelfde.
              </p>
              <p>
                Exchange Online zit standaard in al onze werkplekken. Zo bouw je veilig mailen op een stevige basis: met back-up, tweestapsverificatie en beheer door ons.
              </p>
            </div>
            <Button variant="outline" className="mt-8" asChild>
              <Link to="/diensten/werkplek/exchange-online">
                Meer over Exchange Online
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Zo werkt het */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">Zo werkt het, in 4 situaties</h2>
            <p className="mb-12 text-lg text-muted-foreground">
              Je mailt zoals je gewend bent. De koppeling zorgt dat elke mail op de juiste manier verstuurd wordt.
            </p>
          </div>
          <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-2">
            {situaties.map((s, i) => (
              <motion.div
                key={s.title}
                {...fadeUp}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="rounded-2xl border border-border bg-card p-6 transition-all hover:border-rinqel-blue/30 hover:shadow-lg"
              >
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-hero-gradient text-lg font-bold text-primary-foreground">
                    {i + 1}
                  </div>
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-rinqel-blue/10">
                    <s.icon className="h-5 w-5 text-rinqel-blue" />
                  </div>
                </div>
                <h3 className="mb-2 text-lg font-bold">{s.title}</h3>
                <p className="text-sm text-muted-foreground">{s.description}</p>
              </motion.div>
            ))}
          </div>
          <motion.div
            {...fadeUp}
            className="mx-auto mt-8 flex max-w-5xl items-start gap-4 rounded-2xl border border-rinqel-green/30 bg-rinqel-green/5 p-6"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rinqel-green/10">
              <Smartphone className="h-5 w-5 text-rinqel-green" />
            </div>
            <div>
              <h3 className="font-bold">Onderweg?</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Ook op je telefoon kun je veilig mailen. Zet een kort codewoord in het onderwerp en de mail gaat vanzelf veilig de deur uit. Welk woord dat is, leggen we uit bij de oplevering.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Het verschil */}
      <section className="border-y border-border bg-muted/30 py-16 md:py-24">
        <div className="container">
          <h2 className="mb-12 text-center text-3xl font-bold md:text-4xl">Het verschil</h2>
          <div className="mx-auto grid max-w-5xl gap-8 md:grid-cols-2">
            <div className="rounded-2xl border border-destructive/20 bg-card p-8">
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
                  <XCircle className="h-5 w-5 text-destructive" />
                </div>
                <h3 className="text-xl font-bold text-destructive">Zonder Rinqel</h3>
              </div>
              <ul className="space-y-4">
                {comparison.without.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive/60" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-rinqel-green/30 bg-card p-8 shadow-lg shadow-rinqel-green/5">
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rinqel-green/10">
                  <CheckCircle2 className="h-5 w-5 text-rinqel-green" />
                </div>
                <h3 className="text-xl font-bold text-rinqel-green">Met Rinqel</h3>
              </div>
              <ul className="space-y-4">
                {comparison.with.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-rinqel-green" />
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Voordelen */}
      <section className="py-16 md:py-24">
        <div className="container">
          <h2 className="mb-12 text-center text-3xl font-bold md:text-4xl">Wat levert het je op?</h2>
          <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-2 lg:grid-cols-3">
            {voordelen.map((v) => (
              <div key={v.title} className="rounded-2xl border border-border bg-card p-6 transition-all hover:border-rinqel-blue/30 hover:shadow-lg">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-rinqel-blue/10">
                  <v.icon className="h-6 w-6 text-rinqel-blue" />
                </div>
                <h3 className="mb-2 text-lg font-bold">{v.title}</h3>
                <p className="text-sm text-muted-foreground">{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NTA 7516 */}
      <section className="border-y border-border bg-muted/30 py-16 md:py-24">
        <div className="container">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">NTA 7516? Wij regelen de techniek.</h2>
            <p className="mb-12 text-lg text-muted-foreground">
              NTA 7516 is de Nederlandse norm voor veilig mailen in de zorg. Mail je gezondheidsgegevens? Dan wordt van je verwacht dat je aan deze norm voldoet.
            </p>
          </div>
          <div className="mx-auto grid max-w-5xl gap-8 md:grid-cols-2">
            <div className="rounded-2xl border border-rinqel-green/30 bg-card p-8 shadow-lg shadow-rinqel-green/5">
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rinqel-green/10">
                  <ShieldCheck className="h-5 w-5 text-rinqel-green" />
                </div>
                <h3 className="text-xl font-bold text-rinqel-green">Wat wij regelen</h3>
              </div>
              <ul className="space-y-4">
                {wijRegelen.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-rinqel-green" />
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-rinqel-blue/30 bg-card p-8">
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rinqel-blue/10">
                  <ClipboardList className="h-5 w-5 text-rinqel-blue" />
                </div>
                <h3 className="text-xl font-bold text-rinqel-blue">Wat je zelf doet</h3>
              </div>
              <ul className="space-y-4">
                {zelfDoen.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <FileCheck2 className="mt-0.5 h-5 w-5 shrink-0 text-rinqel-blue" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="mx-auto mt-10 max-w-3xl text-center text-lg font-semibold text-foreground">
            Wij helpen je graag op weg, maar de verklaring teken je zelf. Zo werkt de norm nu eenmaal.
          </p>
        </div>
      </section>

      {/* 4 stappen */}
      <section className="py-16 md:py-24">
        <div className="container">
          <motion.div className="mx-auto max-w-3xl text-center" {...fadeUp}>
            <h2 className="text-3xl font-bold md:text-4xl">
              Zo regelen we het, <span className="text-gradient">in 4 stappen</span>
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Jij tekent één contract. Wij doen de rest en leveren het werkend op.
            </p>
          </motion.div>
          <div className="mx-auto mt-12 grid max-w-4xl gap-6 md:grid-cols-2">
            {stappen.map((item, i) => (
              <motion.div
                key={item.step}
                className="flex gap-4 rounded-2xl border border-border bg-card p-6 shadow-card"
                {...fadeUp}
                transition={{ duration: 0.4, delay: i * 0.08 }}
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-hero-gradient text-lg font-bold text-primary-foreground">
                  {item.step}
                </div>
                <div>
                  <h3 className="font-semibold">{item.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Wat kost het */}
      <section className="py-12">
        <div className="container">
          <motion.div className="mx-auto max-w-2xl rounded-2xl border border-border bg-card p-6 text-center shadow-card md:p-8" {...fadeUp}>
            <h2 className="text-lg font-bold">Wat kost het?</h2>
            <p className="mt-4 text-muted-foreground">
              Het abonnement voor veilig mailen sluit je rechtstreeks af bij de leverancier. Zij maken een offerte op maat en sturen hun factuur direct naar jou, zonder tussenkomst van ons. Zo weet je precies waar je aan toe bent.
            </p>
            <p className="mt-3 text-muted-foreground">
              Al klant bij Rinqel? Dan kun je veilig mailen op elk moment toevoegen. Het hoeft dus niet meteen bij de start.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Nog geen klant */}
      <section className="py-12 md:py-16">
        <div className="container">
          <motion.div className="mx-auto max-w-3xl rounded-2xl border border-rinqel-green/30 bg-rinqel-green/5 p-8" {...fadeUp}>
            <h2 className="mb-4 text-2xl font-bold md:text-3xl">Nog geen klant? Welkom!</h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                Wij koppelen veilig mailen alleen aan een Microsoft-omgeving die wij zelf beheren. Zo weten we zeker dat alles goed is ingericht én goed blijft werken. Updates, beveiliging en de koppeling met de leverancier: wij houden het in de gaten.
              </p>
              <p>
                Werk je nu nog met een andere IT-partij? Dan nemen we eerst het beheer van je Microsoft 365 over. Daarna koppelen we veilig mailen.
              </p>
            </div>
            <Button variant="outline" className="mt-6" asChild>
              <Link to="/diensten/werkplek/overstap">
                Hoe werkt de overstap?
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Voor wie */}
      <section className="py-12 md:py-16">
        <div className="container">
          <div className="mx-auto max-w-3xl">
            <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-rinqel-blue/10">
              <Users className="h-6 w-6 text-rinqel-blue" />
            </div>
            <h2 className="mb-4 text-2xl font-bold md:text-3xl">Voor wie is dit?</h2>
            <p className="mb-6 text-lg text-muted-foreground">
              Voor elke zorgverlener die gevoelige informatie mailt. Bijvoorbeeld:
            </p>
            <ul className="grid gap-3 sm:grid-cols-2">
              {doelgroepen.map((d) => (
                <li key={d} className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-rinqel-green" />
                  <span className="text-foreground">{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <FAQ items={veiligMailenZorgFAQ} title="Veelgestelde vragen" />

      {/* CTA */}
      <section className="border-t border-border bg-gradient-to-br from-primary/5 via-rinqel-blue/5 to-rinqel-green/5 py-16 md:py-24">
        <div className="container">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">Benieuwd wat dit voor jouw praktijk betekent?</h2>
            <p className="mb-8 text-lg text-muted-foreground">
              We vertellen je graag hoe het werkt. Geen verkooppraatje, gewoon een eerlijk gesprek over veilig mailen in jouw praktijk.
            </p>
            <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
              <Button variant="hero" size="lg" asChild>
                <TrackedLink to="/contact" trackLabel="Neem contact op" trackLocation="praktijk_veilig_mailen_zorg_cta">
                  Neem contact op
                </TrackedLink>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <a href="tel:+31858223400" onClick={() => trackCTA("Bel ons", "praktijk_veilig_mailen_zorg_cta")}>
                  Bel ons direct
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default PraktijkVeiligMailenZorg;
